import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Row from '../../Row';
import Col from '../../Col';
import InboxHeader from '../InboxHeader/InboxHeader';
import FontAwesomeIcon from '../../Icon';
import InboxItemsList from '../InboxItemsList/InboxItemsList';
import InboxMessageChat from '../InboxMessageChat/InboxMessageChat';
import InboxPropertyAnnouncementsList from '../InboxPropertyAnnouncementsList/InboxPropertyAnnouncementsList';
import InboxSuitesList from '../InboxSuitesList/InboxSuitesList';
import InboxServiceRequestsList from '../InboxServiceRequestsList/InboxServiceRequestsList';
import {
  useInboxAPI,
  UseInboxAPIReturn,
  SendChatMessageResult,
} from 'src/pages/Inbox/CustomHooks/useInboxAPI';
import {
  useModulePermissionsAPI,
  UseModulePermissionsAPIReturn,
} from 'src/SharedCustomHooks/UseModulePermissionsAPI/useModulePermissionsAPI';
import { ModulePermission } from 'src/components/ContactForm/types';
import InboxRefreshIndicator from 'src/components/InboxRefreshIndicator/InboxRefreshIndicator';
import { PATHS } from 'src/common/constants';
import { PAGINATION_KEYS } from 'src/store/slices/persistedUiSlice';
import './inbox-dashboard.scss';

const InboxDashboard = () => {
  const history = useHistory();

  const deepLinkMatch = useRouteMatch<{ referenceId: string }>(
    `${PATHS.INBOX}/:referenceId`
  );

  const [type, setType] = useState('Messages');
  const [search, setSearch] = useState('');
  const [announcement, setAnnouncement] = useState({});
  const [selectedItem, setSelectedItem] = useState<any>({});

  const {
    inboxList,
    loadingInboxList,
    announcementsList,
    loadingAnnouncements,
    suitesList,
    loadingSuites,
    serviceRequestsList,
    loadingServiceRequests,
    chatMessagesList,
    loadingChatMessages,
    pendingChatMessageText,
    propertyAnnouncementsList,
    loadingPropertyAnnouncements,
    sendingVideoChatInvite,
    onFetchInboxList,
    onFetchAnnouncementList,
    onFetchInboxSuitesList,
    onFetchInboxServiceRequestList,
    onFetchChatMessages,
    onFetchChatUserInformation,
    onSendChatMessage,
    onFetchAnnouncementsByProperty,
    onSendVideoChatInvite,
    clearChatMessages,
    clearChatUserInformation,
    clearPropertyAnnouncements,
    markInboxThreadRead,
    prependInboxThread,
  }: UseInboxAPIReturn = useInboxAPI();

  const {
    modulePermissions,
    onFetchModulePermissions,
  }: UseModulePermissionsAPIReturn = useModulePermissionsAPI();

  const allowVideoChat: boolean = modulePermissions.some(
    (permission: ModulePermission) => permission.name === 'Video Chat'
  );

  useEffect(() => {
    loadInboxAndOpenDeepLinkedThread();
  }, []);

  const loadInboxAndOpenDeepLinkedThread = async () => {
    const threads = await fetchInboxThreads('');

    await openDeepLinkedThread(threads);
  };

  const openDeepLinkedThread = async (threads: any[]) => {
    if (!deepLinkMatch) {
      return;
    }

    history.replace(PATHS.INBOX);

    const deepLinkedThread = threads.find(
      (thread: any) => thread.referenceId === deepLinkMatch.params.referenceId
    );

    if (!deepLinkedThread) {
      console.warn(
        '[InboxDashboard] deep-linked thread not found in inbox list:',
        deepLinkMatch.params.referenceId
      );
      return;
    }

    await selectItem(deepLinkedThread);
  };

  const fetchInboxThreads = (search: string): Promise<any[]> => {
    const isFirstLoad: boolean = inboxList.length <= 0;

    return onFetchInboxList({ search, showLoader: isFirstLoad });
  };

  const sendChatMessage = (
    messageText: string,
    attachment: File | null
  ): Promise<SendChatMessageResult> => {
    return onSendChatMessage({
      thread: selectedItem,
      messageText,
      attachment,
      search,
    });
  };

  const onSelectType = (type) => {
    setType(type);
    setSearch('');
    fetchCalls(type, '');
    clearChatMessages();
    setSelectedItem({});
  };

  const onSearch = (e) => {
    setSearch(e.target.value);
    fetchCalls(type, e.target.value);
  };

  const fetchCalls = (type, search) => {
    switch (type) {
      case 'Messages':
        return fetchInboxThreads(search);
      case 'Announcements':
        return fetchAnnouncements(search);
      case 'General':
        return fetchSuites(search);
      case 'Service Request':
        return fetchServiceRequests(search);
      default:
        return;
    }
  };

  const fetchAnnouncements = (search: string): Promise<void> => {
    const isFirstLoad: boolean = announcementsList.length <= 0;

    return onFetchAnnouncementList({ search, showLoader: isFirstLoad });
  };

  const fetchSuites = (search: string): Promise<void> => {
    return onFetchInboxSuitesList({ search, showLoader: true });
  };

  const fetchServiceRequests = (search: string): Promise<void> => {
    const isUnsearchedLoad: boolean = !search;

    return onFetchInboxServiceRequestList({
      search,
      showLoader: isUnsearchedLoad,
    });
  };

  const selectItem = async (data: any) => {
    await onFetchModulePermissions(data.propertyId);

    if (type === 'General' || type === 'Service Request') {
      setSearch('');
    }

    clearChatUserInformation();
    setAnnouncement({});
    clearPropertyAnnouncements();
    clearChatMessages();
    setSelectedItem(data);

    if (
      type === 'Messages' ||
      type === 'General' ||
      type === 'Service Request'
    ) {
      setType('Messages');
      await onFetchChatMessages(data.referenceId, data.referenceType);
      await onFetchChatUserInformation(data.referenceId, data.referenceType);
      await markInboxThreadRead(data.referenceId, data.referenceType);
    } else if (type === 'Announcements') {
      await onFetchAnnouncementsByProperty({ propertyId: data.propertyId });
    }
  };

  const onSetAnnouncement = (announcementObj) => {
    setAnnouncement(announcementObj);
  };

  const closeItem = () => {
    setSelectedItem({});
  };

  const showMobileBackButton: boolean = Boolean(selectedItem.id);

  return (
    <div className="inbox-dashboard">
      <div className="page-heading-row">
        <h2 className="page-heading">Messages</h2>
        <InboxRefreshIndicator />
      </div>
      <Row>
        <Col
          md={4}
          xs={12}
          id={
            (selectedItem.id ||
              type === 'General' ||
              type === 'Service Request') &&
            'mobile_responsive_messagelist'
          }
        >
          <InboxHeader type={type} onSelectType={onSelectType} />
          <InboxItemsList
            type={type}
            inboxList={inboxList}
            loadingInboxList={loadingInboxList}
            announcementsList={announcementsList}
            loadingAnnouncements={loadingAnnouncements}
            loadingPropertyAnnouncements={loadingPropertyAnnouncements}
            searchString={search}
            onSearch={onSearch}
            onSelectItem={selectItem}
            selectedId={selectedItem}
            persistKey={PAGINATION_KEYS.inbox}
          />
        </Col>
        <Col
          md={8}
          xs={12}
          id={selectedItem.id && 'mobile_responsive_chatlist'}
        >
          {((selectedItem && selectedItem.id) ||
            type === 'General' ||
            type === 'Service Request') && (
            <>
              <div
                id="mobile_responsive_backbutton"
                className="mobile-inbox-header"
              >
                <Row>
                  {showMobileBackButton && (
                    <Col xs={2}>
                      <div className="mobile-back-button" onClick={closeItem}>
                        <FontAwesomeIcon
                          glyph="arrow-left"
                          weight="solid"
                          size="medium"
                        />
                      </div>
                    </Col>
                  )}
                  <Col xs={showMobileBackButton ? 10 : 12}>
                    <InboxHeader type={type} onSelectType={onSelectType} />
                  </Col>
                </Row>
              </div>
              {type === 'Messages' && (
                <InboxMessageChat
                  selectedItem={selectedItem}
                  allowVideoChat={allowVideoChat}
                  chatMessagesList={chatMessagesList}
                  loadingChatMessages={loadingChatMessages}
                  pendingChatMessageText={pendingChatMessageText}
                  onSendChatMessage={sendChatMessage}
                  sendingVideoChatInvite={sendingVideoChatInvite}
                  onSendVideoChatInvite={onSendVideoChatInvite}
                />
              )}
              {type === 'Announcements' && (
                <InboxPropertyAnnouncementsList
                  propertyAnnouncementsList={propertyAnnouncementsList}
                  onSetAnnouncement={onSetAnnouncement}
                  announcement={announcement}
                />
              )}
              {type === 'General' && (
                <InboxSuitesList
                  inboxList={inboxList}
                  onStartNewThread={prependInboxThread}
                  suitesList={suitesList}
                  loadingSuites={loadingSuites}
                  searchString={search}
                  onSearch={onSearch}
                  onSelectItem={selectItem}
                />
              )}
              {type === 'Service Request' && (
                <InboxServiceRequestsList
                  inboxList={inboxList}
                  onStartNewThread={prependInboxThread}
                  serviceRequestsList={serviceRequestsList}
                  loadingServiceRequests={loadingServiceRequests}
                  searchString={search}
                  onSearch={onSearch}
                  onSelectItem={selectItem}
                />
              )}
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InboxDashboard;
